import {database, globalSettings} from "../config";
import {toast} from "react-toastify";
import React from "react";
import {numberWithCommas} from "../util";
import {ACCOUNTS, saveAuditLog} from "./audit";
import * as firebase from "firebase/app";
import {postToDiscord} from "../discord";


export function calculateRefine(materials, materialsMap) {
  let finalMaterials = {};
  let refinedMaterials = {};
  Object.keys(materials).forEach(name => {
    let material = materialsMap[name];
    if (!material) {
      console.warn(`Buyback: skipping unrecognized material "${name}"`);
      return;
    }
    let products = material.products;
    let amt = materials[name];
    
    // Skip refining for moon ores and star crystals
    if (material.name && (material.name.includes("Moon Ore") || material.name.includes("Star Crystal"))) {
      finalMaterials[name] = amt;
    } else if (products) {
      Object.keys(products).forEach(productName => {
        let productAmt = amt * products[productName] * (material.refineMultiplier || 50) / 100 * globalSettings.orePriceMultiplier;
        finalMaterials[productName] = (finalMaterials[productName] || 0) + productAmt
      });
      refinedMaterials[name] = amt
    } else {
      finalMaterials[name] = (finalMaterials[name] || 0) + amt
    }
  });
  return [finalMaterials, refinedMaterials];
}

//
// export function calculateTotalBasePrice(materials, materialsMap) {
//   let totalPrice = 0;
//   Object.keys(materials).forEach(name => {
//     let material = materialsMap[name];
//     if(!product) {
//       return
//     }
//     totalPrice += materials[name] * material.price * (material.multiplier/100) * (material.refineMultiplier || 50) / 100 * 0.95 * (product.totalTargetInventory > 0 ? 1.0 : 0.75)
//   })
//   return totalPrice;
// }

export function calculatePricingInfo(material, materialsMap, location) {
  // Safely handle undefined inventory
  if (!material.inventory) {
    material.inventory = {};
  }
  let inventory = material.inventory[location] || 0;
  if(location === "") {
    inventory = material.totalInventory || 0;
  }
  let target = material.totalTargetInventory || 0;
  target *= globalSettings.locationToTargetMultiplier[location];

  let ratio = inventory / target;
  let percent;
  if (isNaN(ratio)) {
    ratio = Infinity
  }
  if(ratio < 0) {
    ratio = 0;
  }
  let demandClass = "demand-none";

  let basePrice = 0;
  let adjustedPrice;
  
  // Special handling for moon ores and star crystals - use direct price
  if (material.name && (material.name.includes("Moon Ore") || material.name.includes("Star Crystal"))) {
    basePrice = material.price;
    // For moon ores, we use the direct price without any multipliers
    adjustedPrice = basePrice;
    // Still calculate demand class for UI purposes
    if (ratio < globalSettings.highDemandRatio) {
      demandClass = "demand-high";
      percent = 100 * ratio / globalSettings.highDemandRatio;
    } else if (ratio < globalSettings.lowDemandRatio) {
      demandClass = "demand-normal";
      percent = 100 * (ratio - globalSettings.highDemandRatio) / (globalSettings.lowDemandRatio - globalSettings.highDemandRatio);
    } else if (ratio < globalSettings.veryLowDemandRatio) {
      demandClass = "demand-low";
      percent = 100 * (ratio - globalSettings.lowDemandRatio) / (globalSettings.veryLowDemandRatio - globalSettings.highDemandRatio);
    }
    // For moon ores, return the price directly without any multipliers
    return {
      inventory: inventory,
      targetInventory: target,
      demandClass: demandClass,
      percentToNextDemandClass: percent,
      basePrice: basePrice,
      adjustedPrice: basePrice
    };
  } else if (material.products) {
    Object.keys(material.products).forEach(name => {
      let product = materialsMap[name];
      let amt = material.products[name];
      let refineMultiplier = (material.refineMultiplier || 50) / 100;
      if (!product) {
        return
      }
      basePrice += amt * refineMultiplier * globalSettings.orePriceMultiplier * calculatePricingInfo(product, materialsMap, location).adjustedPrice
    });
    basePrice *= (material.multiplier || 100) / 100;
    adjustedPrice = basePrice;
  } else {
    basePrice = material.price * (material.multiplier || 100) / 100;
    adjustedPrice = basePrice * globalSettings.noDemandMultiplier;
    if (ratio < globalSettings.highDemandRatio) {
      demandClass = "demand-high";
      percent = 100 * ratio / globalSettings.highDemandRatio;
      adjustedPrice = basePrice * globalSettings.highDemandMultiplier
    } else if (ratio < globalSettings.lowDemandRatio) {
      demandClass = "demand-normal";
      percent = 100 * (ratio - globalSettings.highDemandRatio) / (globalSettings.lowDemandRatio - globalSettings.highDemandRatio);
      adjustedPrice = basePrice * globalSettings.mediumDemandMultiplier
    } else if (ratio < globalSettings.veryLowDemandRatio) {
      demandClass = "demand-low";
      percent = 100 * (ratio - globalSettings.lowDemandRatio) / (globalSettings.veryLowDemandRatio - globalSettings.highDemandRatio);
      adjustedPrice = basePrice * globalSettings.lowDemandMultiplier
    } else if (target > 1 && (ratio < globalSettings.noDemandRatio || material.type !== "planetary")) {
      demandClass = "demand-low";
      percent = 100;
      adjustedPrice = basePrice * globalSettings.veryLowDemandMultiplier
    }
  }

  adjustedPrice *= globalSettings.locationToPriceMultiplier[location];

  return {
    inventory: inventory,
    targetInventory: target,
    demandClass: demandClass,
    percentToNextDemandClass: percent,
    basePrice: basePrice,
    adjustedPrice: adjustedPrice,
  }
}


export function newTransaction(userInfo, materials, location, materialsMap) {
  let [finalMaterials, refinedMaterials] = calculateRefine(materials, materialsMap);
  return database.runTransaction((transaction) => {
    let p = [];
    let totalValue = 0;
    let totalMarketValue = 0;

    let userRef = database.collection("users").doc(userInfo.id);
    return transaction.get(userRef).then((user) => {

      // update targets for all materials donated in parallel
      let valuePromises = [];
      for (let [name, amount] of Object.entries(finalMaterials)) {
        let materialRef = database.collection("materials").doc(name);
        valuePromises.push(transaction.get(materialRef).then((materialDoc) => {
          let material = materialDoc.data();
          let finalValue;
          
          // Special handling for moon ores - use direct price without ANY multipliers
          if (material.name && (material.name.includes("Moon Ore") || material.name.includes("Star Crystal"))) {
            finalValue = material.price * amount; // Direct price, no multipliers
          } else {
            let pricingInfo = calculatePricingInfo(material, materialsMap, location);
            finalValue = pricingInfo.adjustedPrice * amount;
          }
          
          transaction.update(materialRef, {
            totalInventory: firebase.firestore.FieldValue.increment(amount),
            [`inventory.${location}`]: firebase.firestore.FieldValue.increment(amount),
            costBasis: firebase.firestore.FieldValue.increment(finalValue),
          });
          
          return {
            finalValue,
            marketValue: material.price * amount
          };
        }));
      }

      return Promise.all(valuePromises).then(values => {
        totalValue = values.reduce((sum, v) => sum + v.finalValue, 0);
        totalMarketValue = values.reduce((sum, v) => sum + v.marketValue, 0);
        
        let iskValue = totalValue * (1 - globalSettings.pandacoinRatio);
        let pandaValue = totalValue * globalSettings.pandacoinRatio;

        let p = [];
        // create transaction log
        let newLogRef = database.collection("transactions").doc();
        p.push(transaction.set(newLogRef, {
          "actorInfo": userInfo,
          "totalValue": totalValue,
          "iskValue": iskValue,
          "pandaValue": pandaValue,
          "timestamp": Date.now(),
          "location": location,
          "materials": materials,
          "finalMaterials": finalMaterials,
          "refinedMaterials": refinedMaterials,
        }));
        p.push(transaction.update(userRef, {
          "balanceIsk": (user.data().balanceIsk || 0) + iskValue,
          "balancePanda": (user.data().balancePanda || 0) + pandaValue,
          "totalIsk": (user.data().totalIsk || 0) + iskValue,
          "totalPanda": (user.data().totalPanda || 0) + pandaValue,
          "weeklyIsk": (user.data().weeklyIsk || 0) + iskValue,
          "weeklyPanda": (user.data().weeklyPanda || 0) + pandaValue,
        }));
        p.push(saveAuditLog(transaction, userInfo, userInfo, {
          [ACCOUNTS.CORP_INVENTORY]: +totalMarketValue,
          [ACCOUNTS.CORP_LIABILITIES_ISK]: -iskValue,
          [ACCOUNTS.CORP_LIABILITIES_PANDA]: -pandaValue,
          [ACCOUNTS.CORP_BUYBACK_PROFIT]: -(totalMarketValue - iskValue - pandaValue),
        }, {
          transaction: newLogRef.id,
          materials: materials,
          finalMaterials: finalMaterials,
          refinedMaterials: refinedMaterials,
        }));
        return Promise.all(p).then(() => {
          postToDiscord({
            channel: "AUDIT",
            username: "Pandemic Industry",
            content: `${userInfo.name} completed a transaction for ${numberWithCommas(totalValue)} ISK in ${location}: ${JSON.stringify(materials)}`
          });
          toast.success(<div>
            <strong>Transaction complete</strong>
            <ul>
              <li>+{numberWithCommas(iskValue)} ISK</li>
              <li>+{numberWithCommas(pandaValue)} 🐼</li>
              <li>Total: {numberWithCommas(totalValue)}</li>
            </ul>
          </div>);
        })
      })
    });
  })
}

export function undoTransaction(userInfo, transactionID) {
  let transactionRef = database.collection("transactions").doc(transactionID);

  return database.runTransaction((transaction) => {
    let p = [];
    return transaction.get(transactionRef).then(transactionDoc => {
      let transactionData = transactionDoc.data();
      if (transactionData.undone) {
        return Promise.reject("Transaction already undone")
      }
      let userRef = database.collection("users").doc(transactionData.actorInfo.id);
      for (let [name, amount] of Object.entries(transactionData.finalMaterials)) {
        p.push(transaction.update(database.collection("materials").doc(name), {
          "totalInventory": firebase.firestore.FieldValue.increment(-amount),
          [`inventory.${transactionData.location}`]: firebase.firestore.FieldValue.increment(-amount),
        }))
      }
      for (let [name, amount] of Object.entries(transactionData.refinedMaterials)) {
        p.push(transaction.update(database.collection("materials").doc(name), {
          "totalInventory": firebase.firestore.FieldValue.increment(-amount),
          [`inventory.${transactionData.location}`]: firebase.firestore.FieldValue.increment(-amount),
        }))
      }
      return Promise.all(p).then(() => {
        p = [];
        p.push(transaction.update(userRef, {
          "balanceIsk": firebase.firestore.FieldValue.increment(-transactionData.iskValue),
          "balancePanda": firebase.firestore.FieldValue.increment(-transactionData.pandaValue),
          "totalIsk": firebase.firestore.FieldValue.increment(-transactionData.iskValue),
          "totalPanda": firebase.firestore.FieldValue.increment(-transactionData.pandaValue),
        }));
        p.push(transaction.update(transactionRef, {
          "undone": true,
          "undoneTimestamp": Date.now(),
          "undoneUserInfo": userInfo,
        }));
        return Promise.all(p).then(() => {
          postToDiscord({
            channel: "AUDIT",
            username: "Pandemic Industry",
            content: `${userInfo.name} undid transaction ${transactionID} by ${transactionData.actorInfo.name} for ${numberWithCommas(transactionData.totalValue)}`
          });
        });
      });
    });
  });
}

export const ISK_CREDIT = 1;
export const ISK_DEBIT = 2;
export const PANDA_CREDIT = 3;
export const PANDA_DEBIT = 4;
export const PANDA_THEN_ISK = 5;
export const ISK_ADJUST = 6;
export const PANDA_ADJUST = 7;

export function modifyBalance(actorUserInfo, targetUserID, account, type, amount) {
  let userRef = database.collection("users").doc(targetUserID);
  return database.runTransaction((transaction) => {
    return transaction.get(userRef).then((userResult) => {
      let p = [];
      let user = userResult.data();
      let targetUserInfo = {"id": targetUserID, "name": user.name};

      let amountIsk = 0;
      let amountPanda = 0;
      switch (type) {
        case ISK_CREDIT:
        case ISK_ADJUST:
          amountIsk = amount;
          break;
        case ISK_DEBIT:
          amountIsk = -amount;
          break;
        case PANDA_CREDIT:
        case PANDA_ADJUST:
          amountPanda = amount;
          break;
        case PANDA_DEBIT:
          amountPanda = -amount;
          break;
        case PANDA_THEN_ISK:
          amountPanda = -amount;
          if (amount > user.balancePanda) {
            amountPanda = -user.balancePanda;
            amountIsk = -(amount - user.balancePanda);
          }
      }

      let newBalancePanda = user.balancePanda + amountPanda;
      let newBalanceIsk = user.balanceIsk + amountIsk;

      p.push(transaction.update(userRef, {
        balancePanda: newBalancePanda,
        balanceIsk: newBalanceIsk,
      }));
      p.push(transaction.set(database.collection("payouts").doc(), {
        "actorUserInfo": actorUserInfo,
        "targetUserInfo": targetUserInfo,
        "timestamp": Date.now(),
        "amountIsk": amountIsk,
        "amountPanda": amountPanda,
        "account": account,
      }));
      p.push(saveAuditLog(transaction, actorUserInfo, targetUserInfo, {
        [account]: amountIsk + amountPanda
      }));
      return Promise.all(p).then((arg) => {
        return {
          amountIsk: amountIsk,
          amountPanda: amountPanda,
          newBalanceIsk: newBalanceIsk,
          newBalancePanda: newBalancePanda,
        };
      });
    });
  })
}